import type { AppointedRole } from '@trailblazer/shared';
import type { KombiSessionView, KombiTermsAcceptanceView } from '../viewModels';

/** The safe subset consumed by a future Kombi session adapter, mirroring GET /api/me. */
export interface TrailblazerMePayload {
  user_id?: unknown;
  display_name?: unknown;
  roles?: unknown;
  termsOfUse?: unknown;
}

const APPOINTED_ROLES: readonly AppointedRole[] = [
  'Reviewer',
  'ChampionRole',
  'SWATMember',
  'Manager',
  'PlatformOwner',
];

function toKombiTermsAcceptance(payload: unknown): KombiTermsAcceptanceView | null {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return null;
  const terms = payload as Record<string, unknown>;
  const id = terms.currentVersionId;
  const label = terms.currentVersionLabel;
  if (
    typeof terms.acceptedCurrent !== 'boolean' ||
    (id !== null && typeof id !== 'string') ||
    (label !== null && typeof label !== 'string')
  ) {
    return null;
  }
  return {
    acceptedCurrent: terms.acceptedCurrent,
    currentVersionId: id,
    currentVersionLabel: label,
  };
}

/** Missing or malformed terms data is deliberately represented as null, never as accepted. */
export function toKombiSession(payload: TrailblazerMePayload): KombiSessionView {
  return {
    ...(typeof payload.user_id === 'string' ? { userId: payload.user_id } : {}),
    displayName: typeof payload.display_name === 'string' ? payload.display_name : 'Your account',
    roles: Array.isArray(payload.roles)
      ? payload.roles.filter(
          (role): role is AppointedRole =>
            typeof role === 'string' && APPOINTED_ROLES.includes(role as AppointedRole),
        )
      : [],
    termsOfUse: toKombiTermsAcceptance(payload.termsOfUse),
  };
}

/** Only an explicit, well-formed acceptedCurrent:true result admits the live homepage. */
export function hasAcceptedKombiTerms(session: KombiSessionView): boolean {
  return session.termsOfUse?.acceptedCurrent === true;
}
